import { FC, useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    Button,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '../../atomic/organisms';
import { SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIButton } from '../../atomic/atoms';
import CacheQuery from '../../services/query/cache/CacheQuery.services';
import CacheCommand from '../../services/command/cache/CacheCommand.services';
import Swal from 'sweetalert2';
import DeleteIcon from '@mui/icons-material/Delete';
import { RCM } from './redisCacheManagement.classes';

interface RedisKey {
    key: string;
    value: any;
    type: string;
    ttl: number;
    size: number;
}

export const RedisCacheManagement: FC = () => {
    const { t } = useTranslation();
    const [databaseNumber, setDatabaseNumber] = useState<string>('');
    const [keySearch, setKeySearch] = useState<string>('');
    const [redisKeys, setRedisKeys] = useState<RedisKey[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    useEffect(() => {
        const fetchDefaultDatabase = async () => {
            try {
                const response = await CacheQuery.GetDefaultDatabase();

                setDatabaseNumber(response.toString());
            } catch (error) {
                console.error('Error fetching default database:', error);
            }
        };

        fetchDefaultDatabase();
    }, []);

    const handleFilter = async () => {
        if (!databaseNumber.trim()) {
            Swal.fire(t('general.emptyFieldControlText'), '', 'error');
            return;
        }

        const dbNumber = parseInt(databaseNumber);
        if (isNaN(dbNumber) || dbNumber < 0) {
            Swal.fire(t('general.validFieldControlText'), '', 'error');
            return;
        }

        setLoading(true);
        setError('');
        setSelectedKeys([]);

        try {
            let pattern = '*';
            if (keySearch.trim()) {
                pattern = `*${keySearch.trim()}*`;
            }

            const params = {
                databaseNumber: dbNumber,
                pattern: pattern,
                limit: 200000,
            };

            const response = await CacheQuery.GetRedisData(params);
            console.log('API Response:', response);
            setRedisKeys(response.databases[0].keys);
        } catch (error) {
            console.error('Error fetching Redis data:', error);
            setError(t('redisCacheManagement.loading'));
            setRedisKeys([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteKey = async (key: string) => {
        const dbNumber = parseInt(databaseNumber);

        Swal.fire({
            title: t('redisCacheManagement.deleteKey'),
            text: t('redisCacheManagement.deleteConfirmation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await CacheCommand.DeleteRedisKey(dbNumber, key);
                    Swal.fire(t('redisCacheManagement.deleteSuccess'), '', 'success');
                    handleFilter();
                } catch (error) {
                    console.error('Error deleting Redis key:', error);
                    Swal.fire(t('redisCacheManagement.deleteError'), '', 'error');
                }
            }
        });
    };

    const handleBulkDelete = async () => {
        if (selectedKeys.length === 0) {
            Swal.fire(t('redisCacheManagement.noKeysSelected'), '', 'warning');
            return;
        }

        const dbNumber = parseInt(databaseNumber);

        Swal.fire({
            title: t('redisCacheManagement.bulkDeleteTitle'),
            text: t('redisCacheManagement.bulkDeleteConfirmation', { count: selectedKeys.length }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    await CacheCommand.BulkDeleteRedisKeys(dbNumber, selectedKeys);
                    Swal.fire(t('redisCacheManagement.bulkDeleteSuccess'), '', 'success');
                    setSelectedKeys([]);
                    handleFilter();
                } catch (error) {
                    console.error('Error bulk deleting Redis keys:', error);
                    Swal.fire(t('redisCacheManagement.bulkDeleteError'), '', 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedKeys.length === redisKeys.length) {
            setSelectedKeys([]);
        } else {
            setSelectedKeys(redisKeys.map((key) => key.key));
        }
    };

    const handleSelectKey = (key: string, checked: boolean) => {
        if (checked) {
            setSelectedKeys((prev) => [...prev, key]);
        } else {
            setSelectedKeys((prev) => prev.filter((k) => k !== key));
        }
    };

    const handleClearFilters = () => {
        setKeySearch('');
        setDatabaseNumber('');
        setRedisKeys([]);
        setError('');
        setSelectedKeys([]);
    };

    const columns = [
        {
            field: 'select',
            headerName: '',
            width: 50,
            sortable: false,
            renderCell: (params: any) => (
                <Checkbox
                    className={RCM.cellSelectCheckbox}
                    checked={selectedKeys.includes(params.row.key)}
                    onChange={(e) => handleSelectKey(params.row.key, e.target.checked)}
                    size="small"
                />
            ),
        },
        {
            field: 'key',
            headerName: t('redisCacheManagement.key'),
            flex: 1,
            minWidth: 300,
            renderCell: (params: any) => (
                <div className={RCM.cellKey} style={{ wordBreak: 'break-all' }}>
                    {params.value}
                </div>
            ),
        },
        {
            field: 'type',
            headerName: t('redisCacheManagement.type'),
            flex: 0.5,
            minWidth: 100,
        },
        {
            field: 'ttl',
            headerName: t('redisCacheManagement.ttl'),
            flex: 0.5,
            minWidth: 100,
            renderCell: (params: any) => (
                <div className={RCM.cellTtl}>{params.value > 0 ? params.value : 'No TTL'}</div>
            ),
        },
        {
            field: 'actions',
            headerName: t('general.actions'),
            flex: 0.5,
            minWidth: 100,
            renderCell: (params: any) => (
                <Button
                    className={RCM.cellDeleteButton}
                    sx={{ minWidth: '30px !important', height: '30px', borderRadius: '100%' }}
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteKey(params.row.key)}
                ></Button>
            ),
        },
    ];

    return (
        <SCUIContent className={RCM.page}>
            <Grid container spacing={3}>
                <Grid
                    className={RCM.headerSection}
                    size={{
                        xs: 12,
                    }}
                >
                    <Typography className={RCM.title} variant="h4" component="h1" gutterBottom>
                        {t('redisCacheManagement.title')}
                    </Typography>
                    <Typography className={RCM.description} variant="body1" color="textSecondary" gutterBottom>
                        {t('redisCacheManagement.description')}
                    </Typography>
                </Grid>

                <Grid
                    className={RCM.filterSection}
                    size={{
                        xs: 12,
                    }}
                >
                    <Paper className={RCM.filterPaper} sx={{ p: 3 }}>
                        <Grid className={RCM.filterGrid} container spacing={2} alignItems="center">
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 3,
                                }}
                            >
                                <TextField
                                    className={RCM.inputDatabaseNumber}
                                    fullWidth
                                    label={t('redisCacheManagement.databaseNumber')}
                                    placeholder={t('redisCacheManagement.enterDatabaseNumber')}
                                    type="number"
                                    value={databaseNumber}
                                    onChange={(e) => setDatabaseNumber(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleFilter();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 4,
                                }}
                            >
                                <TextField
                                    className={RCM.inputKeySearch}
                                    fullWidth
                                    label={t('redisCacheManagement.keySearch')}
                                    placeholder={t('redisCacheManagement.enterKeySearch')}
                                    type="text"
                                    value={keySearch}
                                    onChange={(e) => setKeySearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleFilter();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid>
                                <SCUIButton
                                    className={RCM.buttonFilter}
                                    text={t('redisCacheManagement.filter')}
                                    style={{ height: '36px' }}
                                    type="primary"
                                    sizeType="fixed"
                                    size="m"
                                    onClick={handleFilter}
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid>
                                <SCUIButton
                                    className={RCM.buttonClear}
                                    text={t('general.clear')}
                                    style={{ height: '36px' }}
                                    type="secondary"
                                    sizeType="fixed"
                                    size="m"
                                    onClick={handleClearFilters}
                                    disabled={loading}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {loading && (
                    <Grid
                        className={RCM.loadingSection}
                        size={{
                            xs: 12,
                        }}
                    >
                        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                            <CircularProgress className={RCM.loadingSpinner} />
                            <Typography className={RCM.loadingText} variant="body1" sx={{ ml: 2 }}>
                                {t('redisCacheManagement.loading')}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {error && (
                    <Grid
                        className={RCM.errorSection}
                        size={{
                            xs: 12,
                        }}
                    >
                        <Alert className={RCM.errorAlert} severity="error">
                            {error}
                        </Alert>
                    </Grid>
                )}

                {!loading && !error && (
                    <Grid
                        className={RCM.keysSection}
                        size={{
                            xs: 12,
                        }}
                    >
                        <Paper className={RCM.keysPaper} sx={{ p: 3 }}>
                            <Box
                                className={RCM.keysHeaderRow}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                            >
                                <Typography className={RCM.keysTitle} variant="h6" gutterBottom>
                                    {t('redisCacheManagement.redisKeys')} ({redisKeys.length})
                                </Typography>
                                {redisKeys.length > 0 && (
                                    <Box className={RCM.keysActions} display="flex" gap={2}>
                                        <FormControlLabel
                                            className={RCM.selectAllControl}
                                            control={
                                                <Checkbox
                                                    checked={
                                                        selectedKeys.length === redisKeys.length && redisKeys.length > 0
                                                    }
                                                    indeterminate={
                                                        selectedKeys.length > 0 &&
                                                        selectedKeys.length < redisKeys.length
                                                    }
                                                    onChange={handleSelectAll}
                                                    size="small"
                                                />
                                            }
                                            label={t('redisCacheManagement.selectAll')}
                                        />
                                        {selectedKeys.length > 0 && (
                                            <SCUIButton
                                                className={RCM.buttonBulkDelete}
                                                text={`${t('redisCacheManagement.bulkDelete')} (${selectedKeys.length})`}
                                                type="error"
                                                sizeType="fixed"
                                                size="m"
                                                onClick={handleBulkDelete}
                                                disabled={loading}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {redisKeys.length > 0 ? (
                                <div className={RCM.dataGridOuter} style={{ height: 600, width: '100%' }}>
                                    <div className={RCM.dataGridWrap}>
                                        <SCUICustomDataGrid
                                            rows={redisKeys}
                                            columns={columns}
                                            getRowId={(row: RedisKey) => row.key}
                                            disableRowSelectionOnClick
                                        />
                                    </div>
                                </div>
                            ) : (
                                <Box className={RCM.emptyState} display="flex" justifyContent="center" alignItems="center" p={3}>
                                    <Typography className={RCM.emptyText} variant="body1" color="textSecondary">
                                        {t('redisCacheManagement.noDataFound')} (Keys count: {redisKeys.length})
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </SCUIContent>
    );
};
